import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { Component } from '../../../core/types/gameobject.types';
import { ComponentDefinition, PropertyDefinition } from '../../../core/types/inspector.types';
import { getComponentDefinition } from '../../constants/components';
import { useSceneStore } from '../../store/sceneStore';
import { useSpineRuntimeStore } from '../../store/spineStore';
import {
  NumberInput,
  Vector2Input,
  SliderInput,
  ColorPicker,
  SpineAssetPicker,
  SpineSkinPicker,
  SpineAnimationPicker,
  SpineAnimationList,
  TexturePropertyEditor,
} from './PropertyEditors';
import { cn } from '../../utils/cn';
import { CascadeSlotInspector } from './PropertyEditors/CascadeSlotInspector';
import { BackendGameInspector } from './PropertyEditors/BackendGameInspector';
import { CascadeSlotPartInspector } from './PropertyEditors/CascadeSlotPartInspector';

interface ComponentSectionProps {
  objectId: string;
  component: Component;
}

export const ComponentSection: React.FC<ComponentSectionProps> = ({
  objectId,
  component,
}) => {
  const { updateComponent, removeComponent } = useSceneStore();
  const [isExpanded, setIsExpanded] = useState(true);

  const definition = getComponentDefinition(component.type);

  // Map child sprite-renderer / text-renderer properties to parent cascade-slot-part properties
  const CHILD_TO_PARENT_MAP: Record<string, Record<string, string>> = {
    'sprite-renderer': {
      texturePath: 'textureAssetPath',
    },
    'text-renderer': {
      text: 'textContent',
      fontSize: 'fontSize',
      fill: 'fontColor',
      fontFamily: 'fontFamily',
      fontWeight: 'fontWeight',
      fontStyle: 'fontStyle',
      align: 'textAlign',
      letterSpacing: 'letterSpacing',
    },
  };

  const syncToParentSlotPart = useCallback(
    (propertyName: string, value: unknown) => {
      const mapping = CHILD_TO_PARENT_MAP[component.type];
      if (!mapping) return;
      const parentKey = mapping[propertyName];
      if (!parentKey) return;

      const state = useSceneStore.getState();
      const obj = state.gameObjects[objectId];
      if (!obj?.parentId) return;

      const parent = state.gameObjects[obj.parentId];
      if (!parent) return;

      const partComponent = parent.components.find((c) => c.type === 'cascade-slot-part');
      if (!partComponent) return;

      updateComponent(obj.parentId, partComponent.id, { [parentKey]: value });
    },
    [objectId, component.type, updateComponent]
  );

  const handlePropertyChange = useCallback(
    (propertyName: string, value: unknown) => {
      updateComponent(objectId, component.id, { [propertyName]: value });
      syncToParentSlotPart(propertyName, value);
    },
    [objectId, component.id, updateComponent, syncToParentSlotPart]
  );

  const handleRemove = useCallback(() => {
    removeComponent(objectId, component.id);
  }, [objectId, component.id, removeComponent]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  // For spine-animator, we need to get the spineAssetId from the sibling spine-renderer component
  const gameObject = useSceneStore((s) => s.gameObjects[objectId]);
  const getSpineAssetIdFromComponent = useCallback((): string | null => {
    // If this component has spineAssetId directly, use it
    if (component.data.spineAssetId) {
      return component.data.spineAssetId as string;
    }
    // Otherwise, look for a sibling spine-renderer component
    if (gameObject) {
      const rendererComponent = gameObject.components.find(
        (c) => c.type === 'spine-renderer'
      );
      if (rendererComponent?.data.spineAssetId) {
        return rendererComponent.data.spineAssetId as string;
      }
    }
    return null;
  }, [component.data.spineAssetId, gameObject]);

  const renderProperty = (property: PropertyDefinition) => {
    const value = component.data[property.name] ?? property.defaultValue;

    switch (property.type) {
      case 'number':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <NumberInput
              value={value as number ?? 0}
              onChange={(v) => handlePropertyChange(property.name, v)}
              min={property.min}
              max={property.max}
              step={property.step}
              disabled={property.disabled}
            />
          </div>
        );

      case 'slider':
        return (
          <div key={property.name}>
            <SliderInput
              value={value as number ?? property.defaultValue ?? 0}
              onChange={(v) => handlePropertyChange(property.name, v)}
              label={property.label}
              min={property.min ?? 0}
              max={property.max ?? 1}
              step={property.step ?? 0.01}
              disabled={property.disabled}
            />
          </div>
        );

      case 'boolean':
        return (
          <div key={property.name} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value as boolean ?? false}
              onChange={(e) => handlePropertyChange(property.name, e.target.checked)}
              disabled={property.disabled}
              className={cn(
                'w-4 h-4 rounded border-zinc-600 bg-zinc-800',
                'text-blue-500 focus:ring-blue-500 focus:ring-offset-0',
                'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            <label className="text-xs text-zinc-400">{property.label}</label>
          </div>
        );

      case 'color':
        return (
          <div key={property.name}>
            <ColorPicker
              value={value as string ?? property.defaultValue ?? '#ffffff'}
              onChange={(v) => handlePropertyChange(property.name, v)}
              label={property.label}
              disabled={property.disabled}
            />
          </div>
        );

      case 'dropdown':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <div className="relative">
              <select
                value={value as string ?? ''}
                onChange={(e) => handlePropertyChange(property.name, e.target.value)}
                disabled={property.disabled}
                className={cn(
                  'w-full px-2 py-1.5 pr-8 text-xs bg-zinc-800 border border-zinc-700 rounded',
                  'text-zinc-300 focus:outline-none focus:border-blue-500',
                  'appearance-none cursor-pointer',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {property.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={12}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
              />
            </div>
          </div>
        );

      case 'vector2':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <Vector2Input
              value={value as { x: number; y: number } ?? { x: 0, y: 0 }}
              onChange={(v) => handlePropertyChange(property.name, v)}
              min={property.min}
              max={property.max}
              step={property.step}
              disabled={property.disabled}
            />
          </div>
        );

      case 'string':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <input
              type="text"
              value={value as string ?? ''}
              onChange={(e) => handlePropertyChange(property.name, e.target.value)}
              disabled={property.disabled}
              className={cn(
                'w-full px-2 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded',
                'text-zinc-300 focus:outline-none focus:border-blue-500',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            />
          </div>
        );

      case 'texture':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <TexturePropertyEditor
              value={
                (component.data.texturePath as string | null) ??
                (value as string | null) ??
                null
              }
              onChange={(path) => {
                handlePropertyChange('textureId', path);
                handlePropertyChange('texturePath', path);
              }}
              onTextureMeta={(w, h) => {
                handlePropertyChange('textureWidth', w);
                handlePropertyChange('textureHeight', h);
              }}
            />
          </div>
        );

      case 'reference':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <div
              className={cn(
                'w-full px-2 py-2 text-xs bg-zinc-800 border border-zinc-700 rounded',
                'text-zinc-500 text-center border-dashed',
                'cursor-pointer hover:border-zinc-600 transition-colors'
              )}
            >
              {value ? String(value) : 'None (Drag asset here)'}
            </div>
          </div>
        );

      case 'spine-asset':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <SpineAssetPicker
              value={value as string | null}
              spineAssetPath={component.data.spineAssetPath as string | null}
              onChange={(assetId, assetPath) => {
                handlePropertyChange('spineAssetId', assetId);
                handlePropertyChange('spineAssetPath', assetPath || null);
              }}
              disabled={property.disabled}
            />
          </div>
        );

      case 'spine-skin':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <SpineSkinPicker
              value={value as string ?? 'default'}
              spineAssetId={component.data.spineAssetId as string | null}
              onChange={(v) => handlePropertyChange(property.name, v)}
              disabled={property.disabled}
            />
          </div>
        );

      case 'spine-animation':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <SpineAnimationPicker
              value={value as string ?? ''}
              spineAssetId={getSpineAssetIdFromComponent()}
              onChange={(v) => handlePropertyChange(property.name, v)}
              disabled={property.disabled}
            />
          </div>
        );

      case 'spine-animation-list':
        return (
          <div key={property.name} className="space-y-1">
            <label className="text-xs text-zinc-500">{property.label}</label>
            <SpineAnimationList
              spineAssetId={getSpineAssetIdFromComponent()}
              instanceId={null}
              currentAnimation={component.data.currentAnimation as string ?? ''}
              isPlaying={component.data.isPlaying as boolean ?? false}
              defaultLoop={component.data.defaultLoop as boolean ?? true}
              onPlay={(animName, loop) => {
                // Batch all play-related properties into a single update to avoid
                // intermediate states that cause the animation effect to stop/restart
                const updates: Record<string, unknown> = {
                  currentAnimation: animName,
                  isPlaying: true,
                };
                if (loop !== undefined) {
                  updates.defaultLoop = loop;
                }
                updateComponent(objectId, component.id, updates);
              }}
              onStop={() => {
                handlePropertyChange('isPlaying', false);
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Custom editor routing for slot component types
  const renderCustomEditor = (): React.ReactNode | null => {
    switch (component.type) {
      case 'cascade-slot-config':
        return <CascadeSlotInspector objectId={objectId} component={component} />;
      case 'backend-game-config':
        return <BackendGameInspector objectId={objectId} component={component} />;
      case 'cascade-slot-part':
        return <CascadeSlotPartInspector gameObjectId={objectId} componentType={component.type} data={component.data} />;
      default:
        return null;
    }
  };

  if (!definition) {
    return (
      <div className="border-b border-zinc-800 p-3">
        <div className="text-sm text-zinc-500">
          Unknown component: {component.type}
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-zinc-800">
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer',
          'hover:bg-zinc-800/50 transition-colors'
        )}
        onClick={toggleExpanded}
      >
        <button className="p-0.5 text-zinc-400 hover:text-zinc-200">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className="mr-1">{definition.icon}</span>
        <span className="text-sm font-medium text-zinc-200 select-none">
          {definition.name}
        </span>
        <div className="flex-1" />
        {/* Enabled checkbox */}
        <input
          type="checkbox"
          checked={component.enabled}
          onChange={(e) => {
            e.stopPropagation();
            updateComponent(objectId, component.id, {});
            // Note: enabled is managed at Component level, not in data
            // This would need a separate action to update component.enabled
          }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'w-4 h-4 rounded border-zinc-600 bg-zinc-800',
            'text-blue-500 focus:ring-blue-500 focus:ring-offset-0',
            'cursor-pointer'
          )}
          title={component.enabled ? 'Enabled' : 'Disabled'}
        />
        {definition.removable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            className={cn(
              'p-1 text-zinc-500 hover:text-red-400 hover:bg-zinc-700 rounded',
              'transition-colors'
            )}
            title="Remove Component"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {renderCustomEditor() ??
            definition.properties
              .filter((property) => !property.hidden)
              .map((property) => renderProperty(property))}
        </div>
      )}
    </div>
  );
};
